const MAX_BUFFERED_CALLS = 50
const MAX_BUFFERED_RUNTIME_ERRORS = 50

let sdkModule = null
let sdkPromise = null
let sentryInitialized = false

const queuedCalls = []
const runtimeErrors = []

let runtimeListenersInstalled = false
let onWindowErrorHandler = null
let onUnhandledRejectionHandler = null

function pushToBuffer(buffer, value, limit) {
  buffer.push(value)
  if (buffer.length > limit) {
    buffer.shift()
  }
}

function isRuntimeAvailable() {
  return sentryInitialized && sdkModule
}

function queueCall(call) {
  pushToBuffer(queuedCalls, call, MAX_BUFFERED_CALLS)
}

function runOrQueue(call) {
  if (isRuntimeAvailable()) {
    return call(sdkModule)
  }

  queueCall(call)
  return undefined
}

function applyScopeContext(scope, context = {}) {
  const extras = context.extras ?? {}
  const tags = context.tags ?? {}

  for (const key in extras) {
    scope.setExtra(key, extras[key])
  }

  for (const key in tags) {
    scope.setTag(key, tags[key])
  }

  if (context.level) {
    scope.setLevel(context.level)
  }
}

function flushQueuedCalls() {
  while (queuedCalls.length) {
    const call = queuedCalls.shift()
    try {
      call(sdkModule)
    } catch (error) {
      console.error('[Sentry] queued call failed', error)
    }
  }
}

function flushRuntimeErrors() {
  for (const item of runtimeErrors.splice(0)) {
    try {
      if (item.type === 'rejection') {
        const reason = item.reason
        if (reason instanceof Error) {
          sdkModule.captureException(reason)
        } else {
          sdkModule.captureMessage(String(reason ?? 'Unhandled rejection'))
        }
        continue
      }

      if (item.error instanceof Error) {
        sdkModule.captureException(item.error)
      } else {
        sdkModule.captureMessage(String(item.message ?? 'Runtime error'))
      }
    } catch (error) {
      console.error('[Sentry] runtime buffer flush failed', error)
    }
  }
}

function removeRuntimeErrorListeners() {
  if (!runtimeListenersInstalled || typeof window === 'undefined') {
    return
  }

  if (onWindowErrorHandler) {
    window.removeEventListener('error', onWindowErrorHandler)
  }

  if (onUnhandledRejectionHandler) {
    window.removeEventListener('unhandledrejection', onUnhandledRejectionHandler)
  }

  runtimeListenersInstalled = false
  onWindowErrorHandler = null
  onUnhandledRejectionHandler = null
}

function installRuntimeErrorListeners() {
  if (runtimeListenersInstalled || typeof window === 'undefined') {
    return
  }

  onWindowErrorHandler = (event) => {
    pushToBuffer(
      runtimeErrors,
      {
        type: 'error',
        error: event.error,
        message: event.message,
      },
      MAX_BUFFERED_RUNTIME_ERRORS,
    )
  }

  onUnhandledRejectionHandler = (event) => {
    pushToBuffer(
      runtimeErrors,
      {
        type: 'rejection',
        reason: event.reason,
      },
      MAX_BUFFERED_RUNTIME_ERRORS,
    )
  }

  window.addEventListener('error', onWindowErrorHandler)
  window.addEventListener('unhandledrejection', onUnhandledRejectionHandler)
  runtimeListenersInstalled = true
}

installRuntimeErrorListeners()

function createSentrySdkFacade(module) {
  const {
    init,
    browserTracingIntegration,
    makeBrowserOfflineTransport,
    makeFetchTransport,
    withScope,
    captureException,
    captureMessage,
    captureEvent,
    setTag,
    setUser,
    setMeasurement,
    addBreadcrumb,
    startSpan,
    startInactiveSpan,
    getClient,
    isInitialized,
    metrics,
  } = module

  return {
    init,
    browserTracingIntegration,
    makeBrowserOfflineTransport,
    makeFetchTransport,
    withScope,
    captureException,
    captureMessage,
    captureEvent,
    setTag,
    setUser,
    setMeasurement,
    addBreadcrumb,
    startSpan,
    startInactiveSpan,
    getClient,
    isInitialized,
    metrics,
  }
}

export async function loadSentrySdk() {
  if (sdkModule) {
    return sdkModule
  }

  if (!sdkPromise) {
    sdkPromise = import('@sentry/vue')
      .then((module) => {
        sdkModule = createSentrySdkFacade(module)
        return sdkModule
      })
      .catch((error) => {
        sdkPromise = null
        throw error
      })
  }

  return sdkPromise
}

export function markSentryInitialized() {
  sentryInitialized = true
  flushQueuedCalls()
  flushRuntimeErrors()
  removeRuntimeErrorListeners()
}

export function isInitialized() {
  if (!sentryInitialized || !sdkModule) {
    return false
  }

  if (typeof sdkModule.isInitialized === 'function') {
    return sdkModule.isInitialized()
  }

  return !!sdkModule.getClient?.()
}

export function getClient() {
  return sdkModule?.getClient?.() || null
}

export function captureException(error, context = null) {
  return runOrQueue((sdk) => {
    if (context) {
      sdk.withScope((scope) => {
        applyScopeContext(scope, context)
        sdk.captureException(error)
      })
      return
    }

    sdk.captureException(error)
  })
}

export function captureMessage(message, context = null) {
  return runOrQueue((sdk) => {
    if (context) {
      sdk.withScope((scope) => {
        applyScopeContext(scope, context)
        sdk.captureMessage(message)
      })
      return
    }

    sdk.captureMessage(message)
  })
}

export function captureEvent(event) {
  return runOrQueue((sdk) => sdk.captureEvent(event))
}

export function setTag(key, value) {
  return runOrQueue((sdk) => sdk.setTag(key, value))
}

export function setUser(user) {
  return runOrQueue((sdk) => sdk.setUser(user))
}

export function setMeasurement(name, value, unit) {
  return runOrQueue((sdk) => sdk.setMeasurement(name, value, unit))
}

export const metrics = {
  count(name, value = 1, options) {
    return runOrQueue((sdk) => sdk.metrics?.count?.(name, value, options))
  },
  gauge(name, value, options) {
    return runOrQueue((sdk) => sdk.metrics?.gauge?.(name, value, options))
  },
  distribution(name, value, options) {
    return runOrQueue((sdk) => sdk.metrics?.distribution?.(name, value, options))
  },
}

export function addBreadcrumb(breadcrumb) {
  return runOrQueue((sdk) => sdk.addBreadcrumb(breadcrumb))
}

export function startSpan(options, callback) {
  if (isRuntimeAvailable() && typeof sdkModule.startSpan === 'function') {
    return sdkModule.startSpan(options, callback)
  }

  return callback()
}

export function startInactiveSpan(options = {}) {
  if (isRuntimeAvailable() && typeof sdkModule.startInactiveSpan === 'function') {
    return sdkModule.startInactiveSpan(options)
  }

  return {
    setAttribute: () => {},
    end: () => {},
  }
}
